import { Suspense, useContext, useEffect } from "react"
import { Link, useNavigate } from "react-router-dom"
import {
  ChannelList,
  useChatContext as useStreamChatContext,
} from "stream-chat-react"
import { Heading } from "@planningcenter/doxy-web"
import { useApiRead } from "source/shared/SessionApiResource"
import { Icon } from "source/shared/components"
import { CurrentGroupContext } from "source/groups/my/groups"

export function ConversationsIndex() {
  return (
    <>
      <div className="d-f jc-sb ai-c mb-2">
        <Heading level={1} size={3} text="Conversations" />
        <Link
          to="new"
          className="minor-btn secondary-btn btn"
          aria-label="New conversation"
        >
          <Icon
            symbol="general#plus"
            className="mr-4p"
            style={{ fontSize: 9 }}
            aria-hidden
          />
          New
        </Link>
      </div>
      <Suspense>
        <ConversationsList />
      </Suspense>
    </>
  )
}

function ConversationsList() {
  const { client } = useStreamChatContext()
  const { data: conversations } = useMyGroupConversations()
  const streamChannelIds = conversations.map(
    (c) => c.relationships.stream_channel.data.id,
  )

  useActiveChannelNavigation(conversations)

  const noChannelsId = "this-ensures-we-return-no-channels"
  const filters =
    streamChannelIds.length > 0
      ? { members: { $in: [client.userID] }, id: { $in: streamChannelIds } }
      : { members: { $in: [client.userID] }, id: { $eq: noChannelsId } }

  const channelEventHandler = async (setChannels, event) => {
    const eventChannel = event.channel

    if (!eventChannel?.id || !streamChannelIds.includes(eventChannel.id)) return

    try {
      const newChannel = client.channel(eventChannel.type, eventChannel.id)
      await newChannel.watch()
      setChannels((channels) => [newChannel, ...channels])
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <ChannelList
      filters={filters}
      setActiveChannelOnMount={false}
      onMessageNew={channelEventHandler}
      onAddedToChannel={channelEventHandler}
    />
  )
}

function useMyGroupConversations() {
  const group = useContext(CurrentGroupContext)
  const sourceAppName = "Groups"
  const sourceType = "Group"
  const sourceId = group.id
  const graphId = [sourceAppName, sourceType, sourceId].join("-")

  return useApiRead(`/chat/v2/me/conversations?filter=group&group=${graphId}`)
}

function useActiveChannelNavigation(conversations) {
  const group = useContext(CurrentGroupContext)
  const navigate = useNavigate()
  const { channel: activeChannel, setActiveChannel } = useStreamChatContext()
  const activeConversation =
    activeChannel &&
    conversations.find(
      (c) => c.relationships.stream_channel.data.id === activeChannel.id,
    )

  useEffect(() => {
    const resetActiveChannelBeforeLeaving = () => setActiveChannel(undefined)
    if (activeConversation)
      navigate(`${group.base_path}/conversations/${activeConversation.id}`)

    return resetActiveChannelBeforeLeaving
  }, [setActiveChannel, activeConversation, navigate])

  return null
}
